import React from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "@/src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/ui/dropdown-menu";

import { CONTENT_TYPE_ICONS } from "./contentTypeIcons";

import type { JSX } from "react";

import type { MediaType } from "../../constants/media";

interface ContentListButtonMenuProps {
  contentType: MediaType;
}

const CREATE_TITLE_KEYS: Partial<Record<MediaType, string>> = {
  live: "createLive",
  hosted: "createContent",
  external: "createExternal",
  fast: "createFast",
};

function ContentListButtonMenu({
  contentType,
}: ContentListButtonMenuProps): JSX.Element {
  const history = useHistory();
  const { t } = useTranslation("editor-content");

  const titleKey = CREATE_TITLE_KEYS[contentType];
  const title = titleKey ? t(titleKey) : "";

  // ContentPage ne lit que `audio_only` dans l'état de navigation.
  const handleCreate = (type: MediaType, isAudioOnly = false): void => {
    history.push({
      pathname: `${type}/new/`,
      state: { audio_only: isAudioOnly },
    });
  };

  const handleButtonClick = (): void => handleCreate(contentType);

  const button = (
    <Button
      onClick={contentType === "hosted" ? undefined : handleButtonClick}
      size="lg"
      type="button"
    >
      <Plus />
      {title}
    </Button>
  );

  if (contentType !== "hosted") return button;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => handleCreate("hosted")}>
          <CONTENT_TYPE_ICONS.video />
          {t("hosted_video")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleCreate("hosted", true)}>
          <CONTENT_TYPE_ICONS.audio />
          {t("hosted_audio")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleCreate("master")}>
          <CONTENT_TYPE_ICONS.master />
          {t("master_video")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ContentListButtonMenu;
